import React, { useCallback } from 'react';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { useGetPlanTrialData } from './use-get-plan-trial-data';
import { HAS_DISMISSED_PLAN_TRIAL_BANNER, useUserSpecificLocalStorage } from '../../../hooks/use-local-storage';
import { messages } from '../messages';
import { getCurrentDateTime } from '../../../services/datetime-service';
import { PageEvents, PageEventSourceStrings, useTrackEvent } from '../../../pages/settings/payment-and-subscription/hooks/use-track-event';

import style from '../header-notification-banner.module.less';

type BillingPlanTrialBannerProps = {
  onUpgradeClick: (billingPlanId: number) => void;
};

export const BillingPlanTrialBanner = ({ onUpgradeClick }: BillingPlanTrialBannerProps) => {
  const { daysLeftInPlanTrial, planTierName, activePlanCode, activeBillingPlanId } = useGetPlanTrialData();
  const planSpecificKey = `${HAS_DISMISSED_PLAN_TRIAL_BANNER}/${activePlanCode}`;
  const [, setDismissedAtTimestamp] = useUserSpecificLocalStorage(planSpecificKey);
  const trackDismiss = useTrackEvent(PageEvents.PLAN_TRIAL_BANNER_DISMISS, PageEventSourceStrings.PLAN_TRIAL_BANNER);
  const trackUpgradeClick = useTrackEvent(PageEvents.PLAN_TRIAL_BANNER_UPGRADE_CLICK, PageEventSourceStrings.PLAN_TRIAL_BANNER);

  const handleClose = useCallback(() => {
    trackDismiss({ planTierName, daysLeftInPlanTrial });
    setDismissedAtTimestamp(getCurrentDateTime());
  }, [setDismissedAtTimestamp, trackDismiss, planTierName, daysLeftInPlanTrial]);

  const handleUpgradeClick = useCallback(() => {
    trackUpgradeClick({ planTierName, daysLeftInPlanTrial });
    onUpgradeClick(activeBillingPlanId);
  }, [onUpgradeClick, trackUpgradeClick, planTierName, daysLeftInPlanTrial, activeBillingPlanId]);

  return (
    <BannerMessageV2
      buttons={
        <Button onClick={handleUpgradeClick} theme={THEME_CONSTANTS.white}>
          <Text textType={TextType.Caption} value={messages.upgradeYourPlan} />
        </Button>
      }
      className={style['banner-padding']}
      content={
        <Text
          formattedMessageValues={{
            daysLeftInTrial: Math.ceil(daysLeftInPlanTrial),
            planTierName,
          }}
          textType={TextType.Paragraph}
          value={messages.trialUpgradeInfo}
        />
      }
      layout={BannerLayout.Default}
      onClose={handleClose}
      status={BannerStatus.Featured}
    />
  );
};
